import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { isFrequencyValidForBand, isBandModeBlocked, normalizeFrequencyInput, colors, fontMono, formatUtcTime, s } from "../lib/theme";

const VOICE_MODES = ["SSB", "FM", "AM"];

// Formatea una duración en milisegundos como "H:MM:SS" o "MM:SS" si dura
// menos de una hora — igual de legible de un vistazo en pantalla pequeña.
function formatDuration(ms) {
  if (ms == null || ms < 0) return "—";
  const totalSeconds = Math.floor(ms / 1000);
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const sec = totalSeconds % 60;
  const pad = (n) => String(n).padStart(2, "0");
  return h > 0 ? `${h}:${pad(m)}:${pad(sec)}` : `${pad(m)}:${pad(sec)}`;
}

export default function OtaMobile({ activity, activityId, socket, occupancy, session, activeBands, activeModes }) {
  const myOccupancy = occupancy.find((o) => o.user_id === session.user.id) || null;

  // Formulario de "Iniciar sesión" — se mantiene también DESPUÉS de parar,
  // con los últimos valores usados, para poder modificarlos rápido y
  // volver a iniciar sin tener que escribirlo todo de cero.
  const [band, setBand] = useState(myOccupancy?.band || activeBands[0]?.id || "");
  const [mode, setMode] = useState(myOccupancy?.mode || "");
  const [frequency, setFrequency] = useState(myOccupancy?.frequency || "");
  const [sigInfo, setSigInfo] = useState(myOccupancy?.sig_info || "");
  const [startError, setStartError] = useState(null);
  const [starting, setStarting] = useState(false);

  const [sessionStats, setSessionStats] = useState(null); // {qsoCount, firstQso, lastQso}
  const [recentQsos, setRecentQsos] = useState([]);

  const [callsign, setCallsign] = useState("");
  const [lookupName, setLookupName] = useState(null);
  const [lookupSource, setLookupSource] = useState(null);
  const [rstSent, setRstSent] = useState("599");
  const [rstReceived, setRstReceived] = useState("599");
  const [locator, setLocator] = useState("");
  const [saveError, setSaveError] = useState(null);
  const callsignRef = useRef(null);

  const modesForBand = activeModes.filter((m) => !isBandModeBlocked(band, m));

  // Al iniciar sesión de verdad, RST por defecto según el modo (599 para
  // CW/digital, 59 para voz) — igual que en el Log normal de escritorio.
  useEffect(() => {
    if (myOccupancy) {
      const def = VOICE_MODES.includes(myOccupancy.mode) ? "59" : "599";
      setRstSent(def);
      setRstReceived(def);
    }
  }, [myOccupancy?.mode]);

  const refreshSession = () => {
    if (!socket) return;
    socket.emit("ota_session", { activityId }, (res) => {
      if (res?.session) setSessionStats(res.session);
    });
  };

  useEffect(() => {
    if (myOccupancy) refreshSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myOccupancy?.band, myOccupancy?.mode]);

  useEffect(() => {
    if (myOccupancy) {
      api.listQsos(activityId, "mine").then((rows) => {
        setRecentQsos(
          rows.filter((q) => q.band === myOccupancy.band && q.mode === myOccupancy.mode && q.logged_at >= myOccupancy.claimed_at)
        );
      }).catch(() => {});
    } else {
      setRecentQsos([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myOccupancy?.band, myOccupancy?.mode]);

  useEffect(() => {
    setLookupName(null);
    setLookupSource(null);
    if (callsign.trim().length < 3) return;
    const t = setTimeout(() => {
      api.lookupCallsign(activityId, callsign.trim()).then((result) => {
        if (result?.name) {
          setLookupName(result.name);
          setLookupSource(result.source);
        }
        if (result?.grid && activity?.require_locator) setLocator(result.grid);
      }).catch(() => {});
    }, 400);
    return () => clearTimeout(t);
  }, [callsign, activityId, activity?.require_locator]);

  const startSession = (e) => {
    e.preventDefault();
    setStartError(null);
    if (!band || !mode || !frequency || !sigInfo.trim()) {
      setStartError("Rellena banda, modo, frecuencia y comentario antes de iniciar.");
      return;
    }
    const freqValue = normalizeFrequencyInput(frequency);
    if (!isFrequencyValidForBand(band, freqValue, mode)) {
      setStartError("Frecuencia no válida para esa banda.");
      return;
    }
    setStarting(true);
    socket.emit("ota_start", { activityId, band, mode, frequency: freqValue, sigInfo: sigInfo.trim() }, (res) => {
      setStarting(false);
      if (res?.error) { setStartError(res.error); return; }
      setSessionStats(null);
      setRecentQsos([]);
    });
  };

  const stopSession = () => {
    if (!window.confirm("¿Parar la actividad? Se liberará la celda que tienes reclamada ahora.")) return;
    socket.emit("ota_stop", { activityId }, () => {});
  };

  const saveQso = async (e) => {
    e.preventDefault();
    setSaveError(null);
    if (!callsign.trim()) return;
    try {
      await api.createQso(activityId, {
        callsign: callsign.trim(), band: myOccupancy.band, mode: myOccupancy.mode,
        rstSent, rstReceived, locator: activity?.require_locator ? locator : undefined,
        sigInfo: myOccupancy.sig_info,
      });
      setCallsign(""); setLocator(""); setLookupName(null);
      refreshSession();
      api.listQsos(activityId, "mine").then((rows) => {
        setRecentQsos(
          rows.filter((q) => q.band === myOccupancy.band && q.mode === myOccupancy.mode && q.logged_at >= myOccupancy.claimed_at)
        );
      }).catch(() => {});
      callsignRef.current?.focus();
    } catch (err) {
      setSaveError(err.message);
    }
  };

  const elapsedMs = sessionStats?.firstQso && sessionStats?.lastQso
    ? new Date(sessionStats.lastQso).getTime() - new Date(sessionStats.firstQso).getTime()
    : null;

  // ---------- Pantalla "Iniciar sesión" (sin celda reclamada) ----------
  if (!myOccupancy) {
    return (
      <div style={{ ...s.page, maxWidth: 480 }}>
        <Link to="/actividades" style={{ color: colors.textFaint, fontSize: 13, textDecoration: "none" }}>← Tus actividades</Link>
        <h1 style={{ fontSize: 21, marginTop: 10, marginBottom: 2 }}>{activity.name}</h1>
        <p style={{ fontSize: 12.5, color: colors.textFaint, marginBottom: 20 }}>
          Actividad OTA — {activity.callsign}
        </p>

        <form onSubmit={startSession} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div>
            <label style={s.label}>BANDA</label>
            <select value={band} onChange={(e) => { setBand(e.target.value); setMode(""); }} style={s.input}>
              <option value="">— elige banda —</option>
              {activeBands.map((b) => <option key={b.id} value={b.id}>{b.label || b.id}</option>)}
            </select>
          </div>
          <div>
            <label style={s.label}>MODO</label>
            <select value={mode} onChange={(e) => setMode(e.target.value)} style={s.input} disabled={!band}>
              <option value="">— elige modo —</option>
              {modesForBand.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div>
            <label style={s.label}>FRECUENCIA (MHz)</label>
            <input value={frequency} onChange={(e) => setFrequency(e.target.value)} placeholder="p. ej. 7.032" style={s.input} inputMode="decimal" />
          </div>
          <div>
            <label style={s.label}>COMENTARIO (SIG_INFO)</label>
            <input
              value={sigInfo} onChange={(e) => setSigInfo(e.target.value.slice(0, 30))}
              placeholder="p. ej. EA1/PO-123" style={s.input}
            />
            <p style={{ fontSize: 11, color: colors.textFaint, marginTop: 4 }}>
              Referencia de lo que estás activando (SOTA/POTA/IOTA/DME...) — hasta 30 caracteres, se usará luego para filtrar el log.
            </p>
          </div>

          {startError && <div style={s.errorBox}>{startError}</div>}

          <button type="submit" disabled={starting} style={{ ...s.btnPrimary, height: 46, fontSize: 15, opacity: starting ? 0.6 : 1 }}>
            {starting ? "Iniciando…" : "▶ Iniciar"}
          </button>
        </form>
      </div>
    );
  }

  // ---------- Pantalla de Log OTA (con celda reclamada) ----------
  return (
    <div style={{ ...s.page, maxWidth: 480, paddingBottom: 40 }}>
      <div style={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        marginBottom: 14, gap: 10,
      }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: colors.text }}>
            {myOccupancy.band} · {myOccupancy.mode}
          </div>
          <div style={{ fontSize: 12, color: colors.amber, fontFamily: fontMono }}>
            {myOccupancy.sig_info}
          </div>
        </div>
        <button onClick={stopSession} style={{ ...s.btnGhost, borderColor: colors.redBorder, color: "#ffb4b4" }}>
          ⏹ Parar actividad
        </button>
      </div>

      <div style={{
        display: "flex", gap: 10, marginBottom: 18,
      }}>
        <div style={{ flex: 1, background: colors.panel, border: `1px solid ${colors.lineStrong}`, borderRadius: 8, padding: "10px 12px", textAlign: "center" }}>
          <div style={{ fontSize: 22, fontWeight: 700, fontFamily: fontMono, color: colors.text }}>{sessionStats?.qsoCount ?? 0}</div>
          <div style={{ fontSize: 10, color: colors.textFaint, letterSpacing: "0.06em" }}>QSO EN ESTA SESIÓN</div>
        </div>
        <div style={{ flex: 1, background: colors.panel, border: `1px solid ${colors.lineStrong}`, borderRadius: 8, padding: "10px 12px", textAlign: "center" }}>
          <div style={{ fontSize: 22, fontWeight: 700, fontFamily: fontMono, color: colors.text }}>{formatDuration(elapsedMs)}</div>
          <div style={{ fontSize: 10, color: colors.textFaint, letterSpacing: "0.06em" }}>1º QSO → ÚLTIMO</div>
        </div>
      </div>

      <form onSubmit={saveQso} style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 22 }}>
        <div style={{ display: "flex", alignItems: "flex-end", gap: 10 }}>
          <div style={{ width: 130, flexShrink: 0 }}>
            <label style={s.label}>INDICATIVO</label>
            <input
              ref={callsignRef} value={callsign}
              onChange={(e) => setCallsign(e.target.value.toUpperCase())}
              style={{ ...s.input, width: "100%", fontSize: 18, textAlign: "center", fontFamily: fontMono, textTransform: "uppercase" }}
              autoFocus autoCapitalize="characters"
            />
          </div>
          <div style={{ flex: 1, minWidth: 0, fontSize: 12.5, color: colors.amber, lineHeight: 1.3 }}>
            {lookupName && (
              <>👤 {lookupName}<br /><span style={{ color: colors.textFaint, fontSize: 10.5 }}>({lookupSource === "qrz" ? "QRZ" : "HamQTH"})</span></>
            )}
          </div>
        </div>

        {/* RST no se muestra — para dejar más hueco visible cuando aparece
            el teclado — pero se sigue calculando solo según el modo (59 en
            voz, 599 en CW/digital) y se guarda igual en el QSO. */}

        {activity?.require_locator && (
          <div>
            <label style={s.label}>LOCATOR/QTH</label>
            <input value={locator} onChange={(e) => setLocator(e.target.value.toUpperCase())} style={s.input} />
          </div>
        )}

        {saveError && <div style={s.errorBox}>{saveError}</div>}

        <button type="submit" disabled={!callsign.trim()} style={{ ...s.btnPrimary, height: 48, fontSize: 16, opacity: !callsign.trim() ? 0.5 : 1 }}>
          ✓ Guardar QSO
        </button>
      </form>

      <div style={{ fontSize: 11, color: colors.textFaint, marginBottom: 8, letterSpacing: "0.06em" }}>
        ÚLTIMOS DE ESTA SESIÓN
      </div>
      {recentQsos.length === 0 && <p style={{ fontSize: 13, color: colors.textFaint }}>Todavía ningún contacto en esta sesión.</p>}
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {recentQsos.slice(0, 8).map((q) => (
          <div key={q.id} style={{
            display: "flex", justifyContent: "space-between", fontSize: 13.5,
            background: colors.panel, border: `1px solid ${colors.line}`, borderRadius: 6, padding: "7px 12px",
          }}>
            <span style={{ fontFamily: fontMono, fontWeight: 700 }}>{q.callsign}</span>
            <span style={{ color: colors.textFaint }}>{formatUtcTime(q.logged_at)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
